"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { DesignProgress } from "@/components/DesignProgress";
import { useCookieConsent } from "@/components/CookieConsent";
import { rememberRoomLink, roomHeaders } from "@/lib/room-client";
import type { QuizAnswers } from "@/lib/pipeline/style-profile";
import type { GeneratedDesign } from "@/lib/pipeline/engine";
import { useRoomAccess } from "./use-room-access";
import { createQuizAnswers, getDefaultElements } from "./quiz-defaults";
import { InviteForm } from "./_components/InviteForm";
import { RoomSetup } from "./_components/RoomSetup";
import { StyleQuiz } from "./_components/StyleQuiz";
import { DesignReveal } from "./_components/DesignReveal";

// The server job has ten minutes, followed by thirty seconds to publish its final status.
const DESIGN_POLL_LIMIT = 210;
const DESIGN_POLL_INTERVAL_MS = 3000;

export default function DesignFunnelPage() {
  const { choices: cookieChoices } = useCookieConsent();
  const [step, setStep] = useState<"invite" | "upload" | "quiz" | "generating" | "reveal">("invite");
  const [inviteCode, setInviteCode] = useState("");
  const verifiedInviteCode = useRef("");
  useEffect(() => {
    if (!cookieChoices?.preferences) return;
    const controller = new AbortController();
    const verify = (code?: string) => fetch("/api/verify-invite", {
      signal: controller.signal, cache: "no-store",
      ...(code ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ inviteCode: code }) } : {}),
    });
    const restoreInvite = async () => {
      try {
        let response = await verify(verifiedInviteCode.current || undefined);
        let data = await response.json();
        // Migrate the earlier consented local-storage preference to a validated cookie.
        if (!data.valid) {
          let legacyCode = "";
          try { legacyCode = localStorage.getItem("iva_invite_code") || ""; } catch { /* Storage may be disabled. */ }
          if (legacyCode) { response = await verify(legacyCode); data = await response.json(); }
        }
        if (controller.signal.aborted) return;
        try { localStorage.removeItem("iva_invite_code"); } catch { /* Storage may be disabled. */ }
        if (response.ok && data.valid && typeof data.inviteCode === "string") {
          verifiedInviteCode.current = data.inviteCode;
          setInviteCode(data.inviteCode);
          if (!new URLSearchParams(window.location.search).has("id")) setStep(current => current === "invite" ? "upload" : current);
        }
      } catch { /* Keep the invite form available if the remembered code cannot be verified. */ }
    };
    void restoreInvite();
    return () => controller.abort();
  }, [cookieChoices?.preferences]);
  const [quizStep, setQuizStep] = useState<number>(1);
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [step, quizStep]);
  const [hasBedding, setHasBedding] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/catalog?summary=1", { signal: controller.signal })
      .then(async response => { if (response.ok) setHasBedding((await response.json()).categories.includes("bedding")); })
      .catch(() => { /* Bedding stays hidden until a real catalog is available. */ });
    return () => controller.abort();
  }, []);

  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [imageAspectRatio, setImageAspectRatio] = useState<string>("16:9");
  const [quizAnswers, setQuizAnswers] = useState<QuizAnswers>(() => createQuizAnswers("living"));
  const roomType = quizAnswers.roomType;

  const [designResult, setDesignResult] = useState<GeneratedDesign | null>(null);
  const { isUnlocked, roomLink, acceptRoomAccess, resetRoomAccess } = useRoomAccess();

  const generationController = useRef<AbortController | null>(null);
  useEffect(() => () => generationController.current?.abort(), []);

  const showGenerationFailure = useCallback((design: GeneratedDesign) => {
    setDesignResult(design);
    if (design.error_code === "room_photo_required") {
      if (design.quiz_answers) setQuizAnswers(design.quiz_answers);
      setPhotoPreview(null);
      setQuizStep(1);
    } else {
      alert(design.error || "Generation failed. Please try again.");
    }
    setStep("upload");
  }, []);

  const pollDesign = useCallback(async (id: string, signal: AbortSignal) => {
    let attempts = 0;
    while (attempts < DESIGN_POLL_LIMIT) {
      await new Promise(resolve => setTimeout(resolve, DESIGN_POLL_INTERVAL_MS));
      if (signal.aborted) return;
      try {
        const res = await fetch(`/api/designs/${id}`, { signal, headers: roomHeaders(id) });
        const data = await res.json();
        if (signal.aborted) return;
        if (data.success && data.design) {
          setDesignResult(data.design);
          acceptRoomAccess(data);
          if (data.design.status === "ready") {
            setStep("reveal");
            return;
          } else if (data.design.status === "failed") {
            showGenerationFailure(data.design);
            return;
          }
        } else if (res.status === 404) {
          alert("This design could not be found.");
          setStep("upload");
          return;
        }
      } catch (err) {
        if (signal.aborted) return;
        console.error("Polling error:", err);
      }
      attempts++;
    }
    alert("Your room is taking longer than expected. Refresh this page to check its progress; your design link is saved in the address bar.");
    setStep("upload");
  }, [acceptRoomAccess, showGenerationFailure]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      const controller = new AbortController();
      generationController.current = controller;
      const openRoom = async () => {
        const key = new URLSearchParams(window.location.hash.slice(1)).get("key");
        if (key) {
          rememberRoomLink(window.location.href);
          const redeemed = await fetch(`/api/designs/${encodeURIComponent(id)}/access`, {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ key }), signal: controller.signal,
          });
          if (!redeemed.ok) throw new Error("This private room link is invalid or no longer available.");
          if (!controller.signal.aborted && redeemed.headers.get("X-IVA-Room-Cookie") === "1") window.history.replaceState(null, "", window.location.pathname + window.location.search);
        }
        return (await fetch(`/api/designs/${encodeURIComponent(id)}`, { signal: controller.signal, headers: roomHeaders(id) })).json();
      };
      openRoom()
        .then(data => {
          if (controller.signal.aborted) return;
          if (data.success && data.design) {
            acceptRoomAccess(data);
            setDesignResult(data.design);
            if (data.design.status === "ready") {
              setStep("reveal");
            } else if (["generating", "queued", "matching"].includes(data.design.status)) {
              setStep("generating");
              void pollDesign(id, controller.signal);
            } else {
              showGenerationFailure(data.design);
            }
          } else {
            alert(data.error || "This design could not be found.");
            setStep("upload");
          }
        })
        .catch(error => {
          if (controller.signal.aborted) return;
          alert(error.message || "Could not open this room. Please try its private link again.");
          setStep("upload");
        });
      return () => controller.abort();
    }
  }, [acceptRoomAccess, pollDesign, showGenerationFailure]);

  const handleVerifyInvite = async () => {
    if (!inviteCode.trim()) return;
    try {
      const res = await fetch("/api/verify-invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ inviteCode }),
      });
      const data = await res.json();
      if (res.ok && data.valid) {
        verifiedInviteCode.current = data.inviteCode;
        setInviteCode(data.inviteCode);
        setStep("upload");
      } else {
        alert("Invalid or expired invite code.");
      }
    } catch {
      alert("Failed to verify invite code.");
    }
  };

  const startGeneration = async () => {
    const codeToSend = inviteCode;
    if (!codeToSend) {
      alert("Please enter your invite code first.");
      setStep("invite");
      return;
    }

    generationController.current?.abort();
    const controller = new AbortController();
    generationController.current = controller;
    setDesignResult(null);
    setStep("generating");
    resetRoomAccess();

    try {
      const res = await fetch("/api/designs", {
        signal: controller.signal,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          photoUrl: photoPreview,
          aspectRatio: imageAspectRatio,
          quizAnswers: { ...quizAnswers, roomType },
          inviteCode: codeToSend
        }),
      });

      const data = await res.json();
      if (controller.signal.aborted) return;

      if (data.success && data.design) {
        acceptRoomAccess(data);
        setDesignResult(data.design);
        window.history.pushState(null, "", res.headers.get("X-IVA-Room-Cookie") === "1" ? `/design?id=${data.design.id}` : data.roomLink);
        if (data.design.status === "ready") {
          setStep("reveal");
        } else if (data.design.status === "failed") {
          showGenerationFailure(data.design);
        } else {
          void pollDesign(data.design.id, controller.signal);
        }
      } else {
        throw new Error(data.error || "Generation error");
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error(err);
      alert(err instanceof Error ? err.message : "Something went wrong generating your design.");
      setStep("upload");
    }
  };

  function restartRoom() {
    setStep("upload");
    setQuizStep(1);
    resetRoomAccess();
    window.history.replaceState(null, "", "/design");
  }

  return (
    <div className="flex flex-col flex-1 bg-paper">
      <Header />
      <main className="flex-1 min-w-0 max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full pb-32">
        {step === "invite" && <InviteForm inviteCode={inviteCode} onCodeChange={setInviteCode} onVerify={handleVerifyInvite} />}
        {step === "upload" && <RoomSetup roomType={roomType} photoPreview={photoPreview}
          photoIssue={designResult?.error_code === "room_photo_required" ? designResult.error : undefined}
          setPhotoPreview={photo => {
            setPhotoPreview(photo);
            if (photo && designResult?.error_code === "room_photo_required") {
              setDesignResult(null);
              resetRoomAccess();
              window.history.replaceState(null, "", "/design");
            }
          }} setImageAspectRatio={setImageAspectRatio}
          onRoomTypeChange={roomType => setQuizAnswers(answers => ({ ...answers, roomType, elementsToAdd: getDefaultElements(roomType) }))}
          onContinue={() => setStep("quiz")} />}
        {step === "quiz" && <StyleQuiz quizAnswers={quizAnswers} quizStep={quizStep} hasBedding={hasBedding}
          onAnswersChange={setQuizAnswers} onStepChange={setQuizStep} onBack={() => setStep("upload")} onSubmit={startGeneration} />}
        {step === "generating" && <DesignProgress progress={designResult?.progress} startedAt={designResult?.created_at} hasSavedRoom={Boolean(designResult)} />}
        {step === "reveal" && designResult && <DesignReveal key={designResult.id} design={designResult}
          isUnlocked={isUnlocked} roomLink={roomLink} onAccessChange={acceptRoomAccess} onDesignChange={setDesignResult} onRestart={restartRoom} />}
      </main>
      <Footer />
    </div>
  );
}
